import {
  arrayToEnum,
  assertDefined,
  getIsSoleTrader,
  isEnv,
  sortObjectsArray,
} from '../../../services/common/miscUtils';
import { CustomerType } from './enums';

export type FinancialProductOption = {
  // eslint-disable-next-line no-use-before-define
  value: FinancialProductName;
  label: string;
  duration?: number;
  contractMileage?: number;
  milesTemplateId?: string;
  restrictions?: Partial<QuoteSchema.Schemas.OfferRestriction>[];
};

/**
 * - Do not export and perform any logic reliant on this list or it's
 * derived FinancialProductsEnum in this file.
 */
const _financialProducts = [
  'LVDL',
  'LOUN_FL',
  'LOUN_PR',
  'LOUN_FL_Pack',
  'LOUN_PR_Pack',
  'LLDN_FL',
  'LLDN_FL_WITHOUT_GAVN',
  'LLDN_FL_WITH_GAVN',
  'LOUO_FL',
  'LOUO_FL_WITHOUT_GAVO',
  'LOUO_FL_WITH_GAVO',
  'LLDO_FL',
  'LLDO_FL_WITHOUT_GAVO',
  'LLDO_FL_WITH_GAVO',
  'LLDN_PR',
  'LLDN_PR_WITHOUT_GAVN',
  'LLDN_PR_WITH_GAVN',
  'LOUO_PR',
  'LOUO_PR_WITHOUT_GAVO',
  'LOUO_PR_WITH_GAVO',
  'LLDO_PR',
  'LLDO_PR_WITHOUT_GAVO',
  'LLDO_PR_WITH_GAVO',
  'CBPN_FL',
  'CBPN_FL_WITHOUT_GAVN',
  'CBPN_FL_WITH_GAVN',
  'CBPO_FL',
  'CBPO_FL_WITHOUT_GAVO',
  'CBPO_FL_WITH_GAVO',
  'CBVN_FL',
  'CBVN_FL_WITHOUT_GAVN',
  'CBVN_FL_WITH_GAVN',
  'CBVO_FL',
  'CBVO_FL_WITHOUT_GAVO',
  'CBVO_FL_WITH_GAVO',
  'LCDN_FL',
] as const;

const FinancialProductsEnum = arrayToEnum(_financialProducts);

export type FinancialProductName = (typeof _financialProducts)[number];

const disallowedFinancialProducts = [
  ...[isEnv('prod') && FinancialProductsEnum.CBPN_FL],
  ...[isEnv('prod') && [FinancialProductsEnum.CBPN_FL_WITHOUT_GAVN]],
  ...[isEnv('prod') && [FinancialProductsEnum.CBPN_FL_WITH_GAVN]],
  ...[isEnv('prod') && [FinancialProductsEnum.CBPO_FL]],
  ...[isEnv('prod') && [FinancialProductsEnum.CBPO_FL_WITHOUT_GAVO]],
  ...[isEnv('prod') && [FinancialProductsEnum.CBPO_FL_WITH_GAVO]],
  ...[isEnv('prod') && [FinancialProductsEnum.CBVN_FL]],
  ...[isEnv('prod') && [FinancialProductsEnum.CBVN_FL_WITHOUT_GAVN]],
  ...[isEnv('prod') && [FinancialProductsEnum.CBVN_FL_WITH_GAVN]],
  ...[isEnv('prod') && [FinancialProductsEnum.CBVO_FL]],
  ...[isEnv('prod') && [FinancialProductsEnum.CBVO_FL_WITHOUT_GAVO]],
  ...[isEnv('prod') && [FinancialProductsEnum.CBVO_FL_WITH_GAVO]],
].filter(Boolean) as FinancialProductName[];

const isFinancialProductSupported = (financialProductName: FinancialProductName) => {
  return (
    _financialProducts.includes(financialProductName) && !disallowedFinancialProducts.includes(financialProductName)
  );
};

export const supportedFinancialProducts = _financialProducts.filter((productName) => {
  return isFinancialProductSupported(productName);
});

/**
 * - Products listed by decreasing priority.
 * - Will affect default selection of financial products on quotation page.
 */
export const financialProductsPriorityMapNewCar = {
  [CustomerType.INDIVIDUAL]: [FinancialProductsEnum.LOUN_PR_Pack],
  [CustomerType.LEGAL_ENTITY]: [FinancialProductsEnum.LOUN_FL, FinancialProductsEnum.LCDN_FL],
};

/**
 * - Products listed by decreasing priority.
 * - Will affect default selection of financial products on quotation page.
 */
export const financialProductsPriorityMapUsedCar = {
  [CustomerType.INDIVIDUAL]: [
    FinancialProductsEnum.LOUO_PR,
    FinancialProductsEnum.LOUO_PR_WITHOUT_GAVO,
    FinancialProductsEnum.LOUO_PR_WITH_GAVO,
  ],
  [CustomerType.LEGAL_ENTITY]: [
    FinancialProductsEnum.LOUO_FL,
    FinancialProductsEnum.LOUO_FL_WITHOUT_GAVO,
    FinancialProductsEnum.LOUO_FL_WITH_GAVO,
  ],
};

const getOffersSortedByPriority = (
  offers: QuoteSchema.Schemas.FinancialProductOfferQuotationTemplate[],
  customerType: CustomerType,
  isCarUsed: boolean
) => {
  const financialProductsPriorityMap = isCarUsed
    ? financialProductsPriorityMapUsedCar
    : financialProductsPriorityMapNewCar;

  return sortObjectsArray(offers, ({ productName }) => {
    const index = financialProductsPriorityMap[customerType].findIndex((priorityProductName) => {
      return productName?.includes(priorityProductName);
    });

    const foundInPriorityArray = index !== -1;

    if (foundInPriorityArray) return index;

    return Infinity;
  });
};

export const isFinancialProductAllowingWarrantyExtension = (productName: FinancialProductName | string) => {
  return productName.includes('WITH_GAV');
};

/**
 * - Offers are first sorted by priority of pre-selection determined by `financialProductsPriorityMap`.
 * - Then filtered according to `isFinancialProductSupported`.
 * - And parsed into option objects for use in UI 'select' component.
 */
export const getSortedSupportedFinancialProductOptionsFromOffers = (
  offers: QuoteSchema.Schemas.FinancialProductOfferQuotationTemplate[],
  customerType: CustomerType,
  isCarUsed: boolean,
  legalEntity?: QuoteSchema.Schemas.EntityLegalFormType
): FinancialProductOption[] => {
  const sortedSupportedOffers = getOffersSortedByPriority(offers, customerType, isCarUsed).filter((offer) => {
    const productName = offer.productName as FinancialProductName;

    if (!isFinancialProductSupported(productName)) {
      return false;
    }

    if (productName.includes(FinancialProductsEnum.LCDN_FL) && customerType === CustomerType.LEGAL_ENTITY) {
      if (legalEntity && getIsSoleTrader(legalEntity)) {
        return false;
      }
    }

    return true;
  });

  return sortedSupportedOffers.map((offer) => ({
    value: assertDefined(offer.productName as FinancialProductName, 'Offer productName is missing'),
    label: assertDefined(offer.productDescription, 'Offer label is missing'),
    duration: offer.calculation?.duration,
    contractMileage: offer.calculation?.contractMileage,
    milesTemplateId: offer.milesTemplateId,
    restrictions: offer.restrictions,
  }));
};

export const isLvdlProduct = (productName: FinancialProductName | string) => {
  return productName.includes(FinancialProductsEnum.LVDL);
};

export const isLouProduct = (productName: FinancialProductName | string) => {
  return [
    FinancialProductsEnum.LOUN_FL,
    FinancialProductsEnum.LOUN_PR,
    FinancialProductsEnum.LOUO_FL,
    FinancialProductsEnum.LOUO_PR,
  ].some((_productName) => productName.includes(_productName));
};

export const isLouPrProduct = (productName: FinancialProductName | string) => {
  return [FinancialProductsEnum.LOUN_PR, FinancialProductsEnum.LOUO_PR].some((_productName) =>
    productName.includes(_productName)
  );
};

export const isProductAllowingOverruledRV = (productName: FinancialProductName | string) => {
  return [
    FinancialProductsEnum.LLDN_FL,
    FinancialProductsEnum.LLDN_PR,
    FinancialProductsEnum.LLDO_FL,
    FinancialProductsEnum.LLDO_PR,
    FinancialProductsEnum.LCDN_FL,
  ].some((_productName) => {
    return productName.includes(_productName);
  });
};

export const isCreditBailProduct = (productName: FinancialProductName | string) => {
  return [
    FinancialProductsEnum.CBVO_FL,
    FinancialProductsEnum.CBPO_FL,
    FinancialProductsEnum.CBPN_FL,
    FinancialProductsEnum.CBVN_FL,
  ].some((_productName) => productName.includes(_productName));
};

export const isProductForNewCar = (productName: FinancialProductName | string) => {
  return /^[A-Z]{3}N/.test(productName);
};

export const isProductForOldCar = (productName: FinancialProductName | string) => {
  return /^[A-Z]{3}O/.test(productName);
};

export const isCreditBailProductForOldCar = (productName: FinancialProductName | string) => {
  return isCreditBailProduct(productName) && isProductForOldCar(productName);
};

export const isProductAllowingDamagesWaiver = (
  productName: FinancialProductName | string,
  customerType: CustomerType
) => {
  const isPrivateCustomer = customerType === CustomerType.INDIVIDUAL;
  return (
    isLouProduct(productName) &&
    !(isCreditBailProduct(productName) || (isPrivateCustomer && isLouPrProduct(productName)))
  );
};

export const isLCDProduct = (productName: FinancialProductName | string) => {
  return productName.includes(FinancialProductsEnum.LCDN_FL);
};

export const isPushVoProduct = (productName: FinancialProductName | string) => {
  return [
    FinancialProductsEnum.LOUO_FL,
    FinancialProductsEnum.LOUO_PR,
    FinancialProductsEnum.CBVO_FL,
    FinancialProductsEnum.CBPO_FL,
  ].some((_productName) => productName.includes(_productName));
};
